import React from 'react';
import PropTypes from 'prop-types';
import {
  Page,
  View,
  Text,
} from '@react-pdf/renderer';

import TablePageHeader from '../../components/TablePageHeader';
import BottomPagination from '../../components/BottomPagination';
import DraftOverlay from '../../components/DraftOverlay';

import { commaEvery3rdChar, formatSeriesName } from '../../../../../../utils';
import { chainedRangeLabels, displayBreakpointHeader } from './breakpointHeaders';

export default function Breakpoints({
  reportData,
  sortedBreakpoints,
  totalPageCount,
  exhibitNumber,
  priorCumulative = 0,
}) {
  const tableDataRaw = reportData.BreakpointAnalyses;

  // Sort rows by Ownership Class
  const ownershipList = tableDataRaw.breakpoints['Ownership Class'] || [];
  const sortedIndices = ownershipList
    .map((cls, idx) => ({ cls, idx }))
    .sort((a, b) => a.cls.localeCompare(b.cls))
    .map(obj => obj.idx);

  const tableData = {
    breakpoints: {},
    incrementalValuePerShare: {},
    totalValuePerShare: {},
  };

  Object.keys(tableDataRaw.breakpoints).forEach(key => {
    tableData.breakpoints[key] = sortedIndices.map(i => tableDataRaw.breakpoints[key][i]);
  });
  Object.keys(tableDataRaw.incrementalValuePerShare).forEach(key => {
    tableData.incrementalValuePerShare[key] = sortedIndices.map(i => tableDataRaw.incrementalValuePerShare[key][i]);
  });
  Object.keys(tableDataRaw.totalValuePerShare).forEach(key => {
    tableData.totalValuePerShare[key] = sortedIndices.map(i => tableDataRaw.totalValuePerShare[key][i]);
  });

  const table1Headers = [
    'Ownership class',
    'Total shares\noutstanding',
    'Original\nissue price',
  ];

  const table2Headers = [
    'Ownership class',
    'Incremental value per share',
  ];

  const table3Headers = [
    'Ownership class',
    'Total value per share',
  ];

  function returnBreakpointData(breakpointName, breakpointType, rowIndex) {
    if (['incrementalValuePerShare', 'totalValuePerShare'].includes(breakpointType)) {
      return tableData[breakpointType][Object.keys(tableData[breakpointType]).find((key) => key.includes(breakpointName))][rowIndex] ?
        `$${tableData[breakpointType][Object.keys(tableData[breakpointType])
          .find((key) => key.includes(breakpointName))][rowIndex].toFixed(4)}` : '—';
    }
    return tableData[breakpointType][Object.keys(tableData[breakpointType]).find((key) => key.includes(breakpointName))][rowIndex] ?
      `$${commaEvery3rdChar(tableData[breakpointType][Object.keys(tableData[breakpointType])
        .find((key) => key.includes(breakpointName))][rowIndex].toFixed(0))}` : '—';
  }

  function returnCellData(dataName, rowIndex) {
    if (dataName === 'Ownership class') return formatSeriesName(tableData.breakpoints['Ownership Class'][rowIndex]);
    if (dataName === 'Total shares\noutstanding') return commaEvery3rdChar(tableData.breakpoints['Total Shares Outstanding'][rowIndex]);
    if (dataName === 'Original\nissue price') {
      return tableData.breakpoints['Original Issue Price'][rowIndex] ?
        `$${parseFloat(tableData.breakpoints['Original Issue Price'][rowIndex]).toFixed(4).toLocaleString('en-US', {
          minimumFractionDigits: 4,
          maximumFractionDigits: 4,
        })}` : 'N/A';
    }
    return '';
  }

  const incrementalValuesArr = [...sortedBreakpoints.map((breakpointName) => tableData.breakpoints[breakpointName].reduce((a, b) => a + b, 0))];
  const incrementalByName = Object.fromEntries(
    sortedBreakpoints.map((name, index) => [name, incrementalValuesArr[index]]),
  );
  const headerLabels = chainedRangeLabels(sortedBreakpoints, incrementalByName, priorCumulative);

  function returnIncrementalValue(columnName, columnIndex, isBreakpoint) {
    if (isBreakpoint) {
      return `$${commaEvery3rdChar(incrementalValuesArr[columnIndex].toFixed(0))}`;
    }
    if (columnName === 'Total shares\noutstanding') {
      return commaEvery3rdChar(tableData.breakpoints['Total Shares Outstanding'].reduce((a, b) => a + b, 0));
    }
    return '';
  }

  function returnCumulativeValue(columnIndex) {
    const pageSum = [...incrementalValuesArr.slice(0, columnIndex + 1)].reduce((a, b) => a + b, 0);
    return `$${commaEvery3rdChar((pageSum + (priorCumulative || 0)).toFixed(0))}`;
  }

  function columnWidth(index) {
    if (index === 0) return '160px';
    if (index === 1) return '88px';
    return '88px';
  }

  const footnote = reportData.footnoteData.bpAnalysis;

  const rowLength = tableData.breakpoints['Ownership Class'].length + (footnote ? Math.ceil(footnote.length / 230) : 0);

  const reduceRowHeight = () => {
    if (rowLength <= 7) return true;
    return false;
  };

  const reduceRowHeightPage2 = () => {
    if (rowLength > 9) return true;
    return false;
  };

  const splitPages = () => {
    if (rowLength > 6) return 3;  // Split into 3 pages if more than 6 rows
    return false; // Do not split if 6 or fewer rows (all tables fit on one page)
  };

  const breakpointColumnWidth = 808 / sortedBreakpoints.length;


  return (
    <>
      <Page
        size="TABLOID"
        orientation="landscape"
        style={{ position: 'relative', backgroundColor: '#EAEAEB' }}
        >
        <TablePageHeader companyName={reportData.companyInfo.companyName} pageName={`Breakpoint Analysis   |   Exhibit ${exhibitNumber}`} />
        <View
          style={{
            marginTop: splitPages() ? '86px' : '56px',
            marginLeft: '32px',
            display: 'flex',
            flexDirection: 'column',
            width: '1160px',
            border: '1px',
            borderColor: '#AFB1B3',
          }}
        >
          <View
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              height: '24px',
              backgroundColor: '#0C2617',
            }}
            >
            <Text
              style={{
                fontFamily: 'RobotoMed',
                fontSize: '10px',
                color: '#8AB9A4',
                paddingLeft: '16px',
                letterSpacing: '0.08px',
                marginRight: '8px',
                width: '160px',
                paddingTop: '6px',
                borderBottom: '1px',
                borderColor: '#CCCCCC',
                height: '24px',
              }}
            >
              (USD$)
            </Text>
            <Text
              style={{
                fontFamily: 'RobotoMed',
                fontSize: '10px',
                color: '#E1E2EC',
                paddingLeft: '16px',
                marginRight: '8px',
                letterSpacing: '0.08px',
                width: '176px',
                paddingTop: '6px',
                borderBottom: '1px',
                borderColor: '#CCCCCC',
                height: '24px',
              }}
            >
              Capitalization details
            </Text>
            <Text
              style={{
                fontFamily: 'RobotoMed',
                fontSize: '10px',
                color: '#E1E2EC',
                paddingLeft: '16px',
                letterSpacing: '0.08px',
                width: '812px',
                paddingTop: '6px',
                borderBottom: '1px',
                borderColor: '#CCCCCC',
                height: '24px',
              }}
            >
              Breakpoint analysis
            </Text>
          </View>
          <View
            style={{
              display: 'flex',
              flexDirection: 'row',
              height: '40px',
              backgroundColor: '#307157',
              borderBottom: '1px',
              borderColor: '#CCCCCC',
            }}
            >
            {table1Headers.map((dataName, index) => (
              <Text
                key={dataName}
                style={{
                  fontFamily: 'RobotoMed',
                  fontSize: '10px',
                  color: '#E1E2EC',
                  width: columnWidth(index),
                  marginTop: 'auto',
                  paddingBottom: '4px',
                  paddingLeft: index === 0 ? '8px' : '2px',
                  paddingRight: '2px',
                  letterSpacing: '0.4px',
                  ...index !== 0 && { textAlign: 'right' },
                }}
              >
                {dataName}
              </Text>
            ))}
            {sortedBreakpoints.map((breakpointName) => (
              <Text
                key={breakpointName}
                style={{
                  fontFamily: 'RobotoMed',
                  fontSize: '9px',
                  color: '#E1E2EC',
                  width: `${breakpointColumnWidth}px`,
                  marginTop: 'auto',
                  paddingBottom: '4px',
                  paddingLeft: '6px',
                  paddingRight: '6px',
                  letterSpacing: '0.4px',
                  textAlign: 'right',
                  lineHeight: '1.4',
                }}
              >
                {displayBreakpointHeader(breakpointName, headerLabels)}
              </Text>
            ))}
          </View>
          {tableData.breakpoints['Ownership Class'].map((cellData, rowIndex) => (
            <View
              key={cellData}
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                height: reduceRowHeight() ? '24px' : '32px',
                backgroundColor: `${rowIndex % 2 ? '#F0F0F3' : '#FAFAFD'}`,
              }}
              >
              {table1Headers.map((dataName, cellIndex) => (
                <Text
                  key={dataName}
                  style={{
                    fontFamily: 'Roboto',
                    fontSize: '10px',
                    color: '#49454F',
                    width: columnWidth(cellIndex),
                    paddingLeft: cellIndex === 0 ? '8px' : '2px',
                    paddingRight: '2px',
                    letterSpacing: '0.4px',
                    ...cellIndex !== 0 && { textAlign: 'right' },
                  }}
                >
                  {returnCellData(dataName, rowIndex) || 'N/A'}
                </Text>
              ))}
              {sortedBreakpoints.map((breakpointName) => (
                <Text
                  key={breakpointName}
                  style={{
                    fontFamily: 'Roboto',
                    fontSize: '10px',
                    color: '#49454F',
                    width: `${808 / sortedBreakpoints.length}px`,
                    paddingLeft: '8px',
                    paddingRight: '2px',
                    letterSpacing: '0.4px',
                    textAlign: 'right',
                  }}
                >
                  {returnBreakpointData(breakpointName, 'breakpoints', rowIndex)}
                </Text>
              ))}
            </View>
          ))}
        </View>
        
        {/* When not splitting pages, show all tables on first page */}
        {!splitPages() && (
          <>
            {/* Incremental value per share table */}
            <View
              style={{
                marginTop: '8px',
                marginLeft: '32px',
                display: 'flex',
                flexDirection: 'column',
                width: '1160px',
                border: '1px',
                borderColor: '#AFB1B3',
              }}
            >
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  height: '24px',
                  backgroundColor: '#307157',
                  borderBottom: '1px',
                  borderColor: '#CCCCCC',
                }}
              >
                {table2Headers.map((dataName, index) => (
                  <Text
                    key={dataName}
                    style={{
                      fontFamily: 'RobotoMed',
                      fontSize: '10px',
                      color: '#E1E2EC',
                      width: index === 0 ? '367px' : '200px',
                      marginTop: 'auto',
                      paddingBottom: '4px',
                      paddingLeft: '16px',
                      paddingRight: '8px',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {dataName}
                  </Text>
                ))}
              </View>
              {tableData.breakpoints['Ownership Class'].map((cellData, rowIndex) => (
                <View
                  key={cellData}
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    height: reduceRowHeight() ? '24px' : '32px',
                    backgroundColor: `${rowIndex % 2 ? '#F0F0F3' : '#FAFAFD'}`,
                  }}
                >
                  {table1Headers.map((dataName, cellIndex) => (
                    <Text
                      key={dataName}
                      style={{
                        fontFamily: 'Roboto',
                        fontSize: '10px',
                        color: '#49454F',
                        width: columnWidth(cellIndex),
                        paddingLeft: cellIndex === 0 ? '8px' : '2px',
                        paddingRight: '2px',
                        letterSpacing: '0.4px',
                        ...cellIndex !== 0 && { textAlign: 'right' },
                      }}
                    >
                      {cellIndex === 0 ? returnCellData(dataName, rowIndex) || 'N/A' : ''}
                    </Text>
                  ))}
                  {sortedBreakpoints.map((breakpointName) => (
                    <Text
                      key={breakpointName}
                      style={{
                        fontFamily: 'Roboto',
                        fontSize: '10px',
                        color: '#49454F',
                        width: `${808 / sortedBreakpoints.length}px`,
                        paddingLeft: '8px',
                        paddingRight: '2px',
                        letterSpacing: '0.4px',
                        textAlign: 'right',
                      }}
                    >
                      {returnBreakpointData(breakpointName, 'incrementalValuePerShare', rowIndex)}
                    </Text>
                  ))}
                </View>
              ))}
            </View>

            {/* Total value per share table */}
            <View style={{ breakInside: 'avoid' }}>
              <View
                style={{
                  marginTop: '8px',
                  marginLeft: '32px',
                  display: 'flex',
                  flexDirection: 'column',
                  width: '1160px',
                  border: '1px',
                  borderColor: '#AFB1B3',
                }}
              >
                <View
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    height: '24px',
                    backgroundColor: '#307157',
                    borderBottom: '1px',
                    borderColor: '#CCCCCC',
                  }}
                >
                  {table3Headers.map((dataName, index) => (
                    <Text
                      key={dataName}
                      style={{
                        fontFamily: 'RobotoMed',
                        fontSize: '10px',
                        color: '#E1E2EC',
                        width: index === 0 ? '367px' : '200px',
                        marginTop: 'auto',
                        paddingBottom: '4px',
                        paddingLeft: '16px',
                        paddingRight: '8px',
                        letterSpacing: '0.5px',
                      }}
                    >
                      {dataName}
                    </Text>
                  ))}
                </View>
                {tableData.breakpoints['Ownership Class'].map((cellData, rowIndex) => (
                  <View
                    key={cellData}
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      height: reduceRowHeight() ? '28px' : '32px',
                      backgroundColor: `${rowIndex % 2 ? '#F0F0F3' : '#FAFAFD'}`,
                    }}
                  >
                    {table1Headers.map((dataName, cellIndex) => (
                      <Text
                        key={dataName}
                        style={{
                          fontFamily: 'Roboto',
                          fontSize: '10px',
                          color: '#49454F',
                          width: columnWidth(cellIndex),
                          paddingLeft: cellIndex === 0 ? '8px' : '6px',
                          paddingRight: '6px',
                          letterSpacing: '0.4px',
                          ...(cellIndex !== 0 && { textAlign: 'right' }),
                        }}
                      >
                        {cellIndex === 0 ? returnCellData(dataName, rowIndex) || 'N/A' : ''}
                      </Text>
                    ))}
                    {sortedBreakpoints.map((breakpointName) => (
                      <Text
                        key={breakpointName}
                        style={{
                          fontFamily: 'Roboto',
                          fontSize: '9px',
                          color: '#49454F',
                          width: `${breakpointColumnWidth}px`,
                          paddingLeft: '6px',
                          paddingRight: '6px',
                          letterSpacing: '0.2px',
                          textAlign: 'right',
                          lineHeight: '1.4',
                        }}
                      >
                        {returnBreakpointData(breakpointName, 'totalValuePerShare', rowIndex)}
                      </Text>
                    ))}
                  </View>
                ))}
              </View>

              {/* INCREMENTAL VALUE ROW */}
              <View
                style={{
                  marginTop: '8px',
                  marginLeft: '32px',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  height: reduceRowHeight() ? '36px' : '40px',
                  backgroundColor: '#9ECEBB',
                  width: '1160px',
                  border: '1px',
                  borderColor: '#AFB1B3',
                  borderBottom: '1px solid #EAE9EC',
                }}
              >
                {/* First cell: Label */}
                <Text
                  style={{
                    fontFamily: 'RobotoMed',
                    fontSize: '10px',
                    color: '#49454F',
                    width: columnWidth(0),
                    paddingLeft: '16px',
                  }}
                >
                  Incremental value
                </Text>

                {/* Second cell: Total shares */}
                <Text
                  style={{
                    fontFamily: 'RobotoMed',
                    fontSize: '10px',
                    color: '#49454F',
                    width: columnWidth(1),
                    textAlign: 'right',
                    paddingRight: '8px',
                  }}
                >
                  {''}
                </Text>

                {/* Third cell: blank */}
                <Text
                  style={{
                    width: columnWidth(2),
                  }}
                >
                    {/* Leave blank */}
                </Text>

                {/* Breakpoint values */}
                {sortedBreakpoints.map((breakpointName, breakpointIndex) => (
                  <Text
                    key={breakpointName}
                    style={{
                      fontFamily: 'Roboto',
                      fontSize: '9px',
                      color: '#49454F',
                      width: `${breakpointColumnWidth}px`,
                      paddingLeft: '4px',
                      paddingRight: '4px',
                      textAlign: 'right',
                      lineHeight: '1.3',
                    }}
                  >
                    {(() => {
                      return returnIncrementalValue(breakpointName, breakpointIndex, true);
                    })()}
                  </Text>
                ))}
              </View>

              {/* CUMULATIVE VALUE ROW */}
              <View
                style={{
                  marginLeft: '32px',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  height: reduceRowHeight() ? '36px' : '40px',
                  backgroundColor: '#9ECEBB',
                  width: '1160px',
                  border: '1px',
                  borderColor: '#AFB1B3',
                  borderTop: 'none',
                }}
              >
                {/* First cell: Label */}
                <Text
                  style={{
                    fontFamily: 'RobotoMed',
                    fontSize: '10px',
                    color: '#49454F',
                    width: columnWidth(0),
                    paddingLeft: '16px',
                  }}
                >
                  Cumulative value
                </Text>

                {/* Second and third cell: empty */}
                <Text style={{ width: columnWidth(1) }}></Text>
                <Text style={{ width: columnWidth(2) }}></Text>

                {/* Breakpoint values */}
                {sortedBreakpoints.map((breakpointName, breakpointIndex) => (
                  <Text
                    key={breakpointName}
                    style={{
                      fontFamily: 'Roboto',
                      fontSize: '9px',
                      color: '#49454F',
                      width: `${breakpointColumnWidth}px`,
                      paddingLeft: '4px',
                      paddingRight: '4px',
                      textAlign: 'right',
                      lineHeight: '1.3',
                    }}
                  >
                    {(() => {
                      const val = returnCumulativeValue(breakpointIndex);
                      return val;
                    })()}
                  </Text>      
                ))}
              </View>
            </View>
          </>
        )}
        
        {/* No need to show Table 2 and 3 on Page 1 when splitting */}
        {splitPages() && (
          <>
            {/* if wanna show anything on page 1 (when pages are splitted) after cap table, add it here -- leaving intentionally for later use  */}
            </>
          )}
        
        {!!footnote && !splitPages() && (
          <Text
            style={{
              fontFamily: 'Roboto',
              fontSize: '8px',
              color: '#49454F',
              letterSpacing: '0.4px',
              marginTop: '16px',
              marginLeft: '90px',
              width: '450px',
              lineHeight: '0.55mm',
            }}
          >
            Footnote:&nbsp;
            {footnote}
          </Text>
        )}
        <BottomPagination
          pageNumber={totalPageCount}
          reportData={reportData}
        />
        {reportData.isDraft && <DraftOverlay />}
      </Page>
      
      {/* Second page for Table 2 (Incremental value per share) */}
      {splitPages() === 3 && (
        <Page
          size="TABLOID"
          orientation="landscape"
          style={{ position: 'relative', backgroundColor: '#EAEAEB' }}
          >
          <TablePageHeader companyName={reportData.companyInfo.companyName} pageName={`Breakpoint Analysis   |   Exhibit ${exhibitNumber}`} />
          
          {/* On the second page, ONLY show the Incremental Value Per Share Table */}
          <View
            style={{
              marginTop: '86px',
              marginLeft: '32px',
              display: 'flex',
              flexDirection: 'column',
              width: '1160px',
              border: '1px',
              borderColor: '#AFB1B3',
            }}
          >
            <View
              style={{
                display: 'flex',
                flexDirection: 'row',
                height: '24px',
                backgroundColor: '#307157',
                borderBottom: '1px',
                borderColor: '#CCCCCC',
              }}
            >
              {table2Headers.map((dataName, index) => (
                <Text
                  key={dataName}
                  style={{
                    fontFamily: 'RobotoMed',
                    fontSize: '10px',
                    color: '#E1E2EC',
                    width: index === 0 ? '367px' : '200px',
                    marginTop: 'auto',
                    paddingBottom: '4px',
                    paddingLeft: '16px',
                    paddingRight: '8px',
                    letterSpacing: '0.5px',
                  }}
                >
                  {dataName}
                </Text>
              ))}
            </View>
             <View
            style={{
              display: 'flex',
              flexDirection: 'row',
              height: '40px',
              backgroundColor: '#307157',
              borderBottom: '1px',
              borderColor: '#CCCCCC',
            }}
            >
            {table1Headers.map((dataName, index) => (
              <Text
                key={dataName}
                style={{
                  fontFamily: 'RobotoMed',
                  fontSize: '10px',
                  color: '#E1E2EC',
                  width: columnWidth(index),
                  marginTop: 'auto',
                  paddingBottom: '4px',
                  paddingLeft: index === 0 ? '8px' : '2px',
                  paddingRight: '2px',
                  letterSpacing: '0.4px',
                  ...index !== 0 && { textAlign: 'right' },
                }}
              >
                {/* {dataName === 'Ownership class' ? '' : dataName || dataName === 'Total shares\noutstanding' ? '' : dataName === 'Original\nissue price' ? '' : dataName} */}
              </Text>
            ))}
            {sortedBreakpoints.map((breakpointName) => (
              <Text
                key={breakpointName}
                style={{
                  fontFamily: 'RobotoMed',
                  fontSize: '9px',
                  color: '#E1E2EC',
                  width: `${breakpointColumnWidth}px`,
                  marginTop: 'auto',
                  paddingBottom: '4px',
                  paddingLeft: '6px',
                  paddingRight: '6px',
                  letterSpacing: '0.4px',
                  textAlign: 'right',
                  lineHeight: '1.4',
                }}
              >
                {displayBreakpointHeader(breakpointName, headerLabels)}
              </Text>
            ))}
          </View>
            {tableData.breakpoints['Ownership Class'].map((cellData, rowIndex) => (
              <View
                key={cellData}
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  height: reduceRowHeight() ? '24px' : '32px',
                  backgroundColor: `${rowIndex % 2 ? '#F0F0F3' : '#FAFAFD'}`,
                }}
              >
                {table1Headers.map((dataName, cellIndex) => (
                  <Text
                    key={dataName}
                    style={{
                      fontFamily: 'Roboto',
                      fontSize: '10px',
                      color: '#49454F',
                      width: columnWidth(cellIndex),
                      paddingLeft: cellIndex === 0 ? '8px' : '2px',
                      paddingRight: '2px',
                      letterSpacing: '0.4px',
                      ...cellIndex !== 0 && { textAlign: 'right' },
                    }}
                  >
                    {cellIndex === 0 ? returnCellData(dataName, rowIndex) || 'N/A' : ''}
                  </Text>
                ))}
                {sortedBreakpoints.map((breakpointName) => (
                  <Text
                    key={breakpointName}
                    style={{
                      fontFamily: 'Roboto',
                      fontSize: '10px',
                      color: '#49454F',
                      width: `${808 / sortedBreakpoints.length}px`,
                      paddingLeft: '8px',
                      paddingRight: '2px',
                      letterSpacing: '0.4px',
                      textAlign: 'right',
                    }}
                  >
                    {returnBreakpointData(breakpointName, 'incrementalValuePerShare', rowIndex)}
                  </Text>
                ))}
              </View>
            ))}
          </View>

          {/* Add footnote to second page if needed */}
          {!!footnote && (
            <Text
              style={{
                fontFamily: 'Roboto',
                fontSize: '8px',
                color: '#49454F',
                letterSpacing: '0.4px',
                marginTop: '16px',
                marginLeft: '90px',
                width: '450px',
                lineHeight: '0.55mm',
              }}
            >
              Footnote:&nbsp;
              {footnote}
            </Text>
          )}
          <BottomPagination
            pageNumber={totalPageCount + 1}
            reportData={reportData}
          />
          {reportData.isDraft && <DraftOverlay />}
        </Page>
      )}

      {/* Third page for Table 3 (Total value per share) */}
      {splitPages() === 3 && (
        <Page
          size="TABLOID"
          orientation="landscape"
          style={{ position: 'relative', backgroundColor: '#EAEAEB' }}
        >
          <TablePageHeader companyName={reportData.companyInfo.companyName} pageName={`Breakpoint Analysis   |   Exhibit ${exhibitNumber}`} />
          
          {/* On the third page, ONLY show the Total Value Per Share Table */}
          <View style={{ breakInside: 'avoid' }}>
            <View
              style={{
                marginTop: '86px',
                marginLeft: '32px',
                display: 'flex',
                flexDirection: 'column',
                width: '1160px',
                border: '1px',
                borderColor: '#AFB1B3',
              }}
            >
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  height: '24px',
                  backgroundColor: '#307157',
                  borderBottom: '1px',
                  borderColor: '#CCCCCC',
                }}
              >
                {table3Headers.map((dataName, index) => (
                  <Text
                    key={dataName}
                    style={{
                      fontFamily: 'RobotoMed',
                      fontSize: '10px',
                      color: '#E1E2EC',
                      width: index === 0 ? '367px' : '200px',
                      marginTop: 'auto',
                      paddingBottom: '4px',
                      paddingLeft: '16px',
                      paddingRight: '8px',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {dataName}
                  </Text>
                ))}
              </View>
                <View
            style={{
              display: 'flex',
              flexDirection: 'row',
              height: '40px',
              backgroundColor: '#307157',
              borderBottom: '1px',
              borderColor: '#CCCCCC',
            }}
            >
            {table1Headers.map((dataName, index) => (
              <Text
                key={dataName}
                style={{
                  fontFamily: 'RobotoMed',
                  fontSize: '10px',
                  color: '#E1E2EC',
                  width: columnWidth(index),
                  marginTop: 'auto',
                  paddingBottom: '4px',
                  paddingLeft: index === 0 ? '8px' : '2px',
                  paddingRight: '2px',
                  letterSpacing: '0.4px',
                  ...index !== 0 && { textAlign: 'right' },
                }}
              >
                {/* {dataName === 'Ownership class' ? '' : dataName || dataName === 'Total shares\noutstanding' ? '' : dataName === 'Original\nissue price' ? '' : dataName} */}
              </Text>
            ))}
            {sortedBreakpoints.map((breakpointName) => (
              <Text
                key={breakpointName}
                style={{
                  fontFamily: 'RobotoMed',
                  fontSize: '9px',
                  color: '#E1E2EC',
                  width: `${breakpointColumnWidth}px`,
                  marginTop: 'auto',
                  paddingBottom: '4px',
                  paddingLeft: '6px',
                  paddingRight: '6px',
                  letterSpacing: '0.4px',
                  textAlign: 'right',
                  lineHeight: '1.4',
                }}
              >
                {displayBreakpointHeader(breakpointName, headerLabels)}
              </Text>
            ))}
          </View>
              {tableData.breakpoints['Ownership Class'].map((cellData, rowIndex) => (
                <View
                  key={cellData}
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    height: reduceRowHeightPage2() ? '24px' : '32px',
                    backgroundColor: `${rowIndex % 2 ? '#F0F0F3' : '#FAFAFD'}`,
                  }}
                >
                  {table1Headers.map((dataName, cellIndex) => (
                    <Text
                      key={dataName}
                      style={{
                        fontFamily: 'Roboto',
                        fontSize: '10px',
                        color: '#49454F',
                        width: columnWidth(cellIndex),
                        paddingLeft: cellIndex === 0 ? '8px' : '2px',
                        paddingRight: '2px',
                        letterSpacing: '0.4px',
                        ...cellIndex !== 0 && { textAlign: 'right' },
                      }}
                    >
                      {cellIndex === 0 ? returnCellData(dataName, rowIndex) || 'N/A' : ''}
                    </Text>
                  ))}
                  {sortedBreakpoints.map((breakpointName) => (
                    <Text
                      key={breakpointName}
                      style={{
                        fontFamily: 'Roboto',
                        fontSize: '10px',
                        color: '#49454F',
                        width: `${808 / sortedBreakpoints.length}px`,
                        paddingLeft: '8px',
                        paddingRight: '2px',
                        letterSpacing: '0.4px',
                        textAlign: 'right',
                      }}
                    >
                      {returnBreakpointData(breakpointName, 'totalValuePerShare', rowIndex)}
                    </Text>
                  ))}
                </View>
              ))}
            </View>
            
            {/* INCREMENTAL VALUE ROW - on third page */}
            <View
              style={{
                marginTop: '8px',
                marginLeft: '32px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                height: reduceRowHeight() ? '36px' : '40px',
                backgroundColor: '#9ECEBB',
                width: '1160px',
                border: '1px',
                borderColor: '#AFB1B3',
                borderBottom: '1px solid #EAE9EC',
              }}
            >
              {/* First cell: Label */}
              <Text
                style={{
                  fontFamily: 'RobotoMed',
                  fontSize: '10px',
                  color: '#49454F',
                  width: columnWidth(0),
                  paddingLeft: '16px',
                }}
              >
                Incremental value
              </Text>

              {/* Second cell: Total shares */}
              <Text
                style={{
                  fontFamily: 'RobotoMed',
                  fontSize: '10px',
                  color: '#49454F',
                  width: columnWidth(1),
                  textAlign: 'right',
                  paddingRight: '8px',
                }}
              >
                {''}
              </Text>

              {/* Third cell: blank */}
              <Text
                style={{
                  width: columnWidth(2),
                }}
              >
                  {/* Leave blank */}
              </Text>

              {/* Breakpoint values */}
              {sortedBreakpoints.map((breakpointName, breakpointIndex) => (
                <Text
                  key={breakpointName}
                  style={{
                    fontFamily: 'Roboto',
                    fontSize: '9px',
                    color: '#49454F',
                    width: `${breakpointColumnWidth}px`,
                    paddingLeft: '4px',
                    paddingRight: '4px',
                    textAlign: 'right',
                    lineHeight: '1.3',
                  }}
                >
                  {(() => {
                    const val = returnIncrementalValue(breakpointName, breakpointIndex, true); 
                    return val;
                  })()}
                </Text>
              ))}
            </View>

            {/* CUMULATIVE VALUE ROW */}
            <View
              style={{
                marginLeft: '32px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                height: reduceRowHeight() ? '36px' : '40px',
                backgroundColor: '#9ECEBB',
                width: '1160px',
                border: '1px',
                borderColor: '#AFB1B3',
                borderTop: 'none',
              }}
            >
              {/* First cell: Label */}
              <Text
                style={{
                  fontFamily: 'RobotoMed',
                  fontSize: '10px',
                  color: '#49454F',
                  width: columnWidth(0),
                  paddingLeft: '16px',
                }}
              >
                Cumulative value
              </Text>

              {/* Second and third cell: empty */}
              <Text style={{ width: columnWidth(1) }}></Text>
              <Text style={{ width: columnWidth(2) }}></Text>

              {/* Breakpoint values */}
              {sortedBreakpoints.map((breakpointName, breakpointIndex) => (
                <Text
                  key={breakpointName}
                  style={{
                    fontFamily: 'Roboto',
                    fontSize: '9px',
                    color: '#49454F',
                    width: `${breakpointColumnWidth}px`,
                    paddingLeft: '4px',
                    paddingRight: '4px',
                    textAlign: 'right',
                    lineHeight: '1.3',
                  }}
                >
                  {(() => {
                    const val = returnCumulativeValue(breakpointIndex);
                    return val;
                  })()}
                </Text>      
              ))}
            </View>
          </View>
          
          {/* Add footnote to third page if needed */}
          {!!footnote && (
            <Text
              style={{
                fontFamily: 'Roboto',
                fontSize: '8px',
                color: '#49454F',
                letterSpacing: '0.4px',
                marginTop: '16px',
                marginLeft: '90px',
                width: '450px',
                lineHeight: '0.55mm',
              }}
            >
              Footnote:&nbsp;
              {footnote}
            </Text>
          )}
          <BottomPagination
            pageNumber={totalPageCount + 2}
            reportData={reportData}
          />
          {reportData.isDraft && <DraftOverlay />}
        </Page>
      )}
    </>
  );
}

Breakpoints.propTypes = {
  reportData: PropTypes.object.isRequired,
  sortedBreakpoints: PropTypes.array.isRequired,
  totalPageCount: PropTypes.number.isRequired,
  exhibitNumber: PropTypes.number.isRequired,
  priorCumulative: PropTypes.number,
};
